/**
 * Spec-display policy — Brand Policy filter for product specs_json.
 *
 * Background. `commerce-keepa-canonical-enrichment` writes Amazon-derived
 * fields (subcategory, buy box price, display group, monthly_sold,
 * variation_count, item_type, etc.) into `products.specs_json`. Those fields
 * are useful internal signals (popularity, cross-check), but Fridge.com is
 * NOT an Amazon storefront — Brand Policy is to never expose Amazon-side
 * classifiers, ASINs, or buy-box pricing on the public PDP. The same policy
 * already hides ASINs.
 *
 * Strategy: filter at DISPLAY time, not at write time. Other code paths
 * (recommendations, FAQs, fact-checking) consume the full specs_json object
 * and need the full data; mutating `products.specs_json` would break them.
 * So we keep the DB row intact and just suppress these keys when building
 * the public spec rows.
 *
 * Used by: components/blocks/technical-specs/index.tsx
 * Also re-applies in: any future component that loops over specs_json.
 */

import { restoreBrandCasing } from '@/lib/brand-casing'

/**
 * Exact key names that are always Amazon-internal — never render publicly.
 * Lower-case match.
 */
export const SPEC_DISPLAY_DENYLIST_EXACT: ReadonlySet<string> = new Set([
  // Amazon classifiers (their taxonomy disagrees with our `products.type`,
  // and seeing two conflicting type rows confused users on the Demuller PDP
  // — May 9, 2026).
  'amazon_subcategory',
  'amazon_display_group',
  'amazon_buy_box_usd',
  'item_type',
  'product_type',
  'brand_classified_as',

  // Pricing surface — we have first-class price/offers UI (see
  // CrossRetailerPriceWidget). Showing list_price_usd as a spec row created
  // a duplicate-price impression and a broken "this is just an Amazon page"
  // signal.
  'list_price_usd',

  // Keepa-internal signals not meant for the buyer
  'monthly_sold',
  'monthly_units_sold',
  'finish_variant_count',
  'variation_count',
  'variation_asins',

  // Follow-up audit (May 9, 2026) — 6 more keys that leaked on hundreds of
  // PDPs. See the PDP audit note + `analysis/` PDP audit report.
  //
  // Raw Amazon breadcrumb e.g. "Home & Garden >Kitchen & Dining >Refrigerators "
  // (93 PDPs). Our nav and category surfaces handle this — don't echo a
  // half-trimmed Amazon trail into the spec table.
  'category',
  // Always empty string in production data (50 PDPs). Renders as a hollow
  // "Keywords: " row.
  'keywords',
  // Always "new" because Phase 7 / Keepa filtering enforces NEW-only — see
  // Five Non-Negotiable Rules. Telling the buyer "Condition: new" is
  // redundant and reads as a marketplace tell. (50 PDPs.)
  'condition',
  // Duplicates `products.description` which is rendered explicitly elsewhere
  // in the PDP (93 PDPs). Two descriptions side-by-side were confusing.
  'description',
  // Internal EPA database id (290 PDPs). Not buyer-facing.
  'energy_star_pd_id',
  // Internal classifier flag (278 PDPs). Implementation signal, not a spec.
  'energy_star_mentioned',

  // FRIDGE-1233 (owner, 09/25/2026: "remove anything like this from our site,
  // anywhere it is remove it"). Feed and sourcing metadata the imports and
  // part harvesters write into specs_json. Measured on active products
  // 09/26/2026: filter_category 349, data_source 348, source_url 8; the part
  // harvester's harvested_at / evidence / spec_gate / offer_gate / image_url 8.
  // These lived in a private list inside getDisplaySpecs, so the Technical
  // Specs block, which reads specs_json through this module, printed them.
  'data_source',
  'filter_category',
  'source_url',
  'harvested_at',
  'evidence',
  'spec_gate',
  'offer_gate',
  'image_url',
])

/**
 * Prefixes that mark a key as internal/source-system metadata.
 * Lower-case prefix match.
 */
export const SPEC_DISPLAY_DENYLIST_PREFIXES: readonly string[] = [
  'amazon_',
  'keepa_',
  'csv_',
  'asin', // any field starting with asin (asin, asin_root, asins, etc.)
  // FRIDGE-1233: affiliate-network feed fields (cj_advertiser 132,
  // cj_catalog_id 125, cj_program_id) and supplier-named part fields
  // (geparts_price_usd, geparts_availability): no supplier name onsite.
  'cj_',
  'geparts_',
]

/**
 * Returns true if a specs_json key should be HIDDEN from the public PDP.
 * Case-insensitive.
 */
export function isSpecKeyHidden(key: string): boolean {
  const k = String(key || '').trim().toLowerCase()
  if (!k) return true
  if (SPEC_DISPLAY_DENYLIST_EXACT.has(k)) return true
  for (const prefix of SPEC_DISPLAY_DENYLIST_PREFIXES) {
    if (k.startsWith(prefix)) return true
  }
  return false
}

/**
 * specs_json with every hidden key removed — for any response that leaves the
 * server (public JSON APIs). Real specs pass through untouched. FRIDGE-1233.
 */
export function publicSpecsJson<T extends Record<string, unknown> | null | undefined>(specs: T): T {
  if (!specs || typeof specs !== 'object' || Array.isArray(specs)) return specs
  return Object.fromEntries(Object.entries(specs).filter(([key]) => !isSpecKeyHidden(key))) as T
}

/**
 * Title-case a string for display ("white" → "White", "WHITE" → "White").
 * Preserves dashes/underscores/slashes as separators within the word.
 *
 * Brand names are the exception (FRIDGE-348, 08/17/2026): a value that IS a
 * brand — or a delimited list of them, e.g. the "Compatible Brands: LG" row on
 * a water-filter PDP — is restored to its canonical casing after the pass, so
 * "LG" never prints as "Lg" and "GE, GE PROFILE" prints "GE, GE Profile".
 * Values that name no known brand are returned exactly as before.
 */
export function titleCaseValue(value: string): string {
  const cased = value
    .toLowerCase()
    .replace(/(^|[\s\-_/])([a-z])/g, (_m, sep, ch) => sep + ch.toUpperCase())
  return restoreBrandCasing(cased)
}

/**
 * Dedupe an array of {label, value} spec rows case-insensitively on the
 * label. When two rows collide, prefer the one whose label looks more
 * "human" (mixed case / Title Case) over an all-lower or all-upper variant.
 *
 * This is what collapses the "Color: White" + "Color: WHITE" double row on
 * the Demuller PDP — `products.color` and `products.specs_json.color` both
 * write a row, but specs_json's value was upper-cased by Keepa.
 */
export function dedupeSpecsByLabel<T extends { label: string; value: string }>(
  specs: readonly T[],
): T[] {
  const seen = new Map<string, T>()
  for (const row of specs) {
    const key = row.label.trim().toLowerCase()
    if (!key) continue
    const existing = seen.get(key)
    if (!existing) {
      // First occurrence — also normalize an all-caps value to Title Case so
      // "WHITE" doesn't sneak through as the only row. Opaque codes are
      // exempt (FRIDGE-280, 08/29/2026): the manuals-mined
      // manufacturer_specs.model_number "CWF440SZ" was rendering "Cwf440sz".
      const cleaned = looksAllCaps(row.value) && !isOpaqueCodeValue(row.value)
        ? { ...row, value: titleCaseValue(row.value) }
        : row
      seen.set(key, cleaned)
      continue
    }
    // Tie-break: prefer the row whose value is NOT all-caps (more human).
    const existingAllCaps = looksAllCaps(existing.value)
    const incomingAllCaps = looksAllCaps(row.value)
    if (existingAllCaps && !incomingAllCaps) {
      seen.set(key, row)
    }
    // else keep existing
  }
  return Array.from(seen.values())
}

function looksAllCaps(s: string): boolean {
  const letters = s.replace(/[^A-Za-z]/g, '')
  if (letters.length < 2) return false
  return letters === letters.toUpperCase()
}

/**
 * A single whitespace-free token containing at least one digit is an opaque
 * code — a model number, SKU, or part identifier ("CWF440SZ", "LRFCS29D6") —
 * not prose. Title-casing destroys it, so every ALL-CAPS display
 * normalization must skip these (FRIDGE-280, 08/29/2026: the manuals-mined
 * manufacturer_specs.model_number, banked on 519 FINs, rendered corrupted).
 * Prose values ("WHITE", "STAINLESS STEEL") contain no digits and still
 * normalize.
 */
export function isOpaqueCodeValue(s: string): boolean {
  const t = s.trim()
  return /^\S+$/.test(t) && /\d/.test(t)
}
